import * as Yup from "yup";
import { Dayjs } from "dayjs";
import { formatDate, formatTime } from "../../../../../../../utils/utils";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import moment from "moment";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const TASK_DIALOG_INITIAL_VALUES: TaskDialogFormValues = {
  subject: "",
  campaignOwner: "",
  priority: "High",
  relatedTo: "",
  status: "Not Started",
  description: "",
  reminder: false,
  repeat: false,
  repeatType: "Daily",
  ends: "never",
  endsAfter: 1,
  dueDate: null,
  reminderAmPm: "AM",
  reminderDate: null,
  reminderHour: "",
  reminderMinute: "",
};

export const TASK_DIALOG_VALIDATION_SCHEMA = Yup.object().shape({
  subject: Yup.string().required("Subject is required").max(100),
  campaignOwner: Yup.string().required("Owner is required"),
  priority: Yup.string().required("Priority is required"),
  relatedTo: Yup.string().required("Related To is required"),
  status: Yup.string().required("Status is required"),
  description: Yup.string().max(150),

  dueDate: Yup.mixed()
    .notRequired()
    .nullable()
    .test("dueDate-valid", "Please enter a valid date", (value) => {
      if (!value) return true;
      const date = dayjs(value as string | Date);
      return date.isValid() && !date.isBefore(dayjs("1900-01-01"));
    }),

  reminderDate: Yup.mixed()
    .notRequired()
    .nullable()
    .test("reminderDate-valid", "Please enter a valid date", (value) => {
      if (!value) return true;
      const date = dayjs(value as string | Date);
      return date.isValid() && !date.isBefore(dayjs("1900-01-01"));
    })
    .test(
      "reminder-before-due",
      "Reminder date should be before or equal to due date",
      function (value) {
        const { dueDate } = this.parent;
        if (!value || !dueDate) return true;

        const reminder = dayjs(value as string | Date).startOf("day");
        const due = dayjs(dueDate as string | Date).startOf("day");
        return reminder.isSameOrBefore(due);
      },
    )
    .test(
      "reminder-time-valid",
      "Enter a valid time in Reminder Date",
      function (value) {
        const { reminderHour, reminderMinute, reminderAmPm } = this.parent;
        if (!value) return true;

        const reminderDate = dayjs(value as string | Date);
        const today = dayjs().startOf("day");

        // Only validate time if date is today
        if (!reminderDate.isSame(today, "day")) return true;

        const hour = parseInt(reminderHour, 10);
        const minute = parseInt(reminderMinute, 10);

        if (isNaN(hour) || isNaN(minute)) return false;

        let fullHour = hour % 12;
        if (reminderAmPm === "PM") fullHour += 12;

        const reminderDateTime = reminderDate.hour(fullHour).minute(minute);
        return reminderDateTime.isAfter(dayjs());
      },
    ),
});
export const PRIORITY_OPTIONS = [
  { value: "Highest" },
  { value: "High" },
  { value: "Medium" },
  { value: "Low" },
  { value: "Lowest" },
];
export const STATUS_OPTIONS = [
  { value: "Not Started" },
  { value: "In Progress" },
  { value: "Completed" },
  { value: "Deferred" },
  { value: "Waiting For Input" },
];
export interface TaskDialogFormValues {
  subject: string;
  campaignOwner: string;
  priority: string;
  relatedTo: string;
  status: string;
  description: string;
  reminder: boolean;
  repeat: boolean;
  repeatType: string;
  ends: string;
  endsAfter: number;
  dueDate: Dayjs | null;
  reminderDate: Dayjs | null;
  reminderAmPm: "AM" | "PM";
  reminderHour: string;
  reminderMinute: string;
}
export const MEETING_DIALOG_INITIAL_VALUES = {
  title: "",
  location: "",
  allDay: false,
  fromDate: null,
  fromTime: null,
  toDate: null,
  toTime: null,
  host: "",
  participants: [],
  relatedTo: "",
  description: "",
  reminder: false,
  repeat: false,
  repeatType: "Daily",
  ends: "never",
  endsAfter: 1,
  dueDate: null,
  reminderAmPm: "AM",
  reminderDate: null,
  reminderHour: "",
  reminderMinute: "",
};

export const MEETING_DIALOG_VALIDATION_SCHEMA = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .max(100, "Title cannot exceed 100 characters"),
  location: Yup.string().nullable(),
  allDay: Yup.boolean(),
  fromDate: Yup.date()
    .required("From Date is required")
    .test("startDate-valid", "Please enter a valid date", (value) => {
      if (!value) return true;
      const date = dayjs(value as string | Date);
      return date.isValid() && !date.isBefore(dayjs("1900-01-01"));
    })
    .test(
      "fromDate-before-or-same-as-toDate",
      "From Date should be before than To Date",
      function (fromDate) {
        const { toDate } = this.parent;
        if (!fromDate || !toDate) return true;
        return dayjs(fromDate).isSameOrBefore(dayjs(toDate), "day");
      },
    ),
  toDate: Yup.date()
    .required("To Date is required")
    .test("endDate-valid", "Please enter a valid date", (value) => {
      if (!value) return true;
      const date = dayjs(value as string | Date);
      return date.isValid() && !date.isBefore(dayjs("1900-01-01"));
    })
    .test(
      "endDate-after-startDate",
      "To Date should be after than From Date",
      function (toDate) {
        const { fromDate } = this.parent;
        if (!toDate || !fromDate) return true;

        const start = dayjs(fromDate);
        const end = dayjs(toDate);

        return end.isSameOrAfter(start, "day"); // checks date only, not time
      },
    ),
  fromTime: Yup.string()
    .required("From Time is required")
    .typeError("Invalid From Time")
    .test("valid-time", "Invalid time", (value) => {
      return !value || dayjs(value).isValid();
    }),
  toTime: Yup.string()
    .required("To Time is required")
    .typeError("Invalid To Time")
    .test("valid-time", "Invalid time", (value) => {
      return !value || dayjs(value).isValid();
    })
    .test(
      "validate-time-if-same-date",
      "To time cannot be same or earlier than From time",
      function (toTimeValue) {
        const { fromDate, toDate, fromTime } = this.parent;

        if (!fromDate || !toDate || !fromTime || !toTimeValue) return true;

        const isSameDate = dayjs(fromDate).isSame(dayjs(toDate), "day");

        if (isSameDate) {
          const from = dayjs(fromTime);
          const to = dayjs(toTimeValue);
          return to.isAfter(from);
        }

        return true; // allow if different dates
      },
    ),
  host: Yup.string().required("Host is required"),
  participants: Yup.array().of(Yup.string().email("Invalid email")).nullable(),
  relatedTo: Yup.string().nullable(),
  description: Yup.string()
    .max(150, "Description cannot exceed 150 characters")
    .nullable(),
  reminderDate: Yup.mixed()
    .nullable()
    .test("reminderDate-valid", "Please enter a valid date", (value) => {
      if (!value) return true;
      const date = dayjs(value as string | Date);
      return date.isValid() && !date.isBefore(dayjs("1900-01-01"));
    })
    .test(
      "reminder-before-toDateTime",
      "Reminder date and time should be before or equal to to date and time",
      function (reminderDate) {
        const { reminderHour, reminderMinute, reminderAmPm, toDate, toTime } =
          this.parent;

        if (
          !reminderDate ||
          reminderHour === "" ||
          reminderMinute === "" ||
          !reminderAmPm ||
          !toDate ||
          !toTime
        ) {
          return true;
        }

        // Parse reminder time
        let hour = Number(reminderHour);
        if (isNaN(hour)) return true;
        if (reminderAmPm === "AM" && hour === 12) hour = 0;
        if (reminderAmPm === "PM" && hour < 12) hour += 12;
        let minute = Number(reminderMinute);
        if (isNaN(minute)) minute = 0;

        const getDateValue = (val: any) => {
          if (!val) return null;
          if (typeof val === "string" || val instanceof Date) return val;
          if (typeof val === "object" && typeof val.toDate === "function")
            return val.toDate();
          return null;
        };

        const reminder = dayjs(getDateValue(reminderDate))
          .hour(hour)
          .minute(minute)
          .second(0)
          .millisecond(0);

        const toDateValue = getDateValue(toDate);
        const toTimeValue = getDateValue(toTime); // assuming toTime is a Date object like in your example

        if (!toDateValue || !toTimeValue) return true;

        // Format toTime into "hh:mm A"
        const toTimeFormatted = moment(toTimeValue).format("hh:mm A");

        // Combine toDate + formatted toTime
        const to = moment(
          `${moment(toDateValue).format("YYYY-MM-DD")} ${toTimeFormatted}`,
          "YYYY-MM-DD hh:mm A",
        );

        // Convert reminder to moment
        const reminderMoment = moment(reminder.toDate());

        // Debug
        console.log("Reminder:", reminderMoment.format("YYYY-MM-DD HH:mm:ss"));
        console.log("To      :", to.format("YYYY-MM-DD HH:mm:ss"));

        // Compare
        return reminderMoment.isSameOrBefore(to);
      },
    )
    .test(
      "reminder-time-valid",
      "Enter a valid time in Reminder Date",
      function (value) {
        const { reminderHour, reminderMinute, reminderAmPm } = this.parent;
        if (!value) return true;

        const reminderDate = dayjs(value as string | Date);
        const today = dayjs().startOf("day");

        // Only validate time if date is today
        if (!reminderDate.isSame(today, "day")) return true;

        const hour = parseInt(reminderHour, 10);
        const minute = parseInt(reminderMinute, 10);

        if (isNaN(hour) || isNaN(minute)) return false;

        let fullHour = hour % 12;
        if (reminderAmPm === "PM") fullHour += 12;

        const reminderDateTime = reminderDate.hour(fullHour).minute(minute);
        return reminderDateTime.isAfter(dayjs());
      },
    ),
  reminderHour: Yup.string().test(
    "reminder-hour-required-if-reminder-date",
    "Please select or enter a valid reminder time",
    function (value) {
      const { reminderDate } = this.parent;

      // Only validate if reminderDate is filled
      if (!reminderDate) return true;

      if (!value || value.trim() === "") return false;

      const num = Number(value);
      return !isNaN(num) && num >= 1 && num <= 12;
    },
  ),
});
export const getMeetingColumns = (campaignDetails?: { name?: string }) => [
  {
    key: "title",
    label: "Title",
    render: (meeting: any) => meeting.title || "-",
  },
  {
    key: "location",
    label: "Location",
    render: (meeting: any) => meeting.location || "-",
  },
  {
    key: "host",
    label: "Host",
    render: (row: any) => {
      const host = row.host;
      if (!host) return "-";

      const { first_name, last_name } = host;

      if (first_name && last_name) return `${first_name} ${last_name}`;
      if (first_name) return first_name;
      if (last_name) return last_name;
      return "-";
    },
  },
  {
    key: "from",
    label: "From",
    render: (meeting: any) =>
      meeting.meetStartTime
        ? `${formatDate(meeting.meetStartTime)} ${formatTime(
            meeting.meetStartTime,
          )}`
        : "-",
  },
  {
    key: "to",
    label: "To",
    render: (meeting: any) =>
      meeting.meetEndTime && meeting.meetEndTime !== "Not Started"
        ? `${formatDate(meeting.meetEndTime)} ${formatTime(meeting.meetEndTime)}`
        : "Not Started",
  },
  {
    key: "relatedTo",
    label: "Related To",
    render: () => campaignDetails?.name || "-",
  },
  {
    key: "reminder",
    label: "Reminder",
    render: (meeting: any) =>
      meeting.reminder
        ? `${formatDate(meeting.reminder)} ${meeting.reminder.padStart(2, "0")}:${meeting.reminder.padStart(2, "0")} ${meeting.reminder}`
        : "-",
  },
];

export const CALL_DIALOG_INITIAL_VALUES = {
  callFor: [],
  relatedTo: "",
  callType: "Outbound",
  outGoingCallStatus: "",
  callStartDate: null,
  callStartTime: null,
  callOwner: "",
  subject: "",
  callPurpose: "",
  callAgenda: "",
};

export const CALL_LOG_DIALOG_INITIAL_VALUES = {
  callFor: [],
  relatedTo: "",
  callType: "Outbound",
  outGoingCallStatus: "",
  callStartDate: null,
  callStartTime: null,
  callDuration: { hh: "", mm: "00", ss: "00" },
  callOwner: "",
  subject: "",
  callPurpose: "",
  callAgenda: "",
  callResult: "",
  description: "",
};

export const CALL_DIALOG_VALIDATION_SCHEMA = () => {
  return Yup.object().shape({
    callFor: Yup.array()
      .min(1, "Call for is required")
      .required("Call for is required"),
    relatedTo: Yup.string().required("Related to is required"),
    callType: Yup.string().required("Call type is required"),
    outGoingCallStatus: Yup.string().nullable(),
    callStartDate: Yup.date().required("Call start date is required"),
    callStartTime: Yup.date()
      .required("Call start time is required")
      .test(
        "past-time",
        "Call Start time must be in the past",
        function (time) {
          const { callStartDate } = this.parent;
          if (!callStartDate || !time) return true;

          const combinedStart = dayjs(callStartDate)
            .hour(dayjs(time).hour())
            .minute(dayjs(time).minute())
            .second(0);

          return combinedStart.isBefore(dayjs());
        },
      ),
    callOwner: Yup.string().required("Call owner is required"),
    subject: Yup.string()
      .max(100, "Subject cannot exceed 100 characters")
      .nullable(),
    callPurpose: Yup.string().nullable(),
    callAgenda: Yup.string()
      .max(500, "Call Agenda cannot exceed 500 characters")
      .nullable(),
    callResult: Yup.string()
      .max(500, "Call Result cannot exceed 500 characters")
      .nullable(),
    callDuration: Yup.object()
      .shape({
        hh: Yup.string().matches(/^\d{1,2}$/, "Invalid hours"),
        mm: Yup.string()
          .matches(/^\d{1,2}$/, "Invalid minutes")
          .test("valid-mm", "Minutes must be between 0 and 59", (val) => {
            const num = parseInt(val || "0", 10);
            return num >= 0 && num < 60;
          }),
        ss: Yup.string()
          .matches(/^\d{1,2}$/, "Invalid seconds")
          .test("valid-ss", "Seconds must be between 0 and 59", (val) => {
            const num = parseInt(val || "0", 10);
            return num >= 0 && num < 60;
          }),
      })
      .required("Call duration is required")
      .test(
        "non-zero-duration",
        "Call duration must be greater than 0",
        (value) => {
          if (!value) return false;
          const hh = parseInt(value.hh || "0", 10);
          const mm = parseInt(value.mm || "0", 10);
          const ss = parseInt(value.ss || "0", 10);
          return hh > 0 || mm > 0 || ss > 0;
        },
      )
      .test(
        "duration-not-more-than-diff",
        "Call duration cannot be greater than time passed since call start",
        function (value) {
          const { callStartDate, callStartTime } = this.parent;
          if (!value || !callStartDate || !callStartTime) return true;

          const start = dayjs(callStartDate)
            .hour(dayjs(callStartTime).hour())
            .minute(dayjs(callStartTime).minute())
            .second(dayjs(callStartTime).second());

          const now = dayjs();
          if (!start.isValid() || start.isAfter(now)) return true;

          const diffInSeconds = now.diff(start, "second");

          const durationInSeconds =
            parseInt(value.hh || "0", 10) * 3600 +
            parseInt(value.mm || "0", 10) * 60 +
            parseInt(value.ss || "0", 10);

          return durationInSeconds <= diffInSeconds;
        },
      ),
    description: Yup.string()
      .max(500, "Description cannot exceed 500 characters")
      .nullable(),
  });
};

export const CALL_DIALOG_VALIDATION_SCHEMA_WITTHOUT_IS_LOG_CALL = () => {
  return Yup.object().shape({
    callFor: Yup.array()
      .min(1, "Call for is required") // ✅ Enforces at least 1 item
      .required("Call for is required"),
    relatedTo: Yup.string().required("Related to is required"),
    callType: Yup.string().required("Call type is required"),
    outGoingCallStatus: Yup.string().nullable(),
    callStartDate: Yup.date().required("Call start date is required"),
    callStartTime: Yup.date()
      .required("Call start time is required")
      .typeError("Invalid call start time")
      .test(
        "future-time",
        "Call Start time must be in the future",
        function (time) {
          const { callStartDate } = this.parent;
          if (!callStartDate || !time) return true;

          const combinedStart = dayjs(callStartDate)
            .hour(dayjs(time).hour())
            .minute(dayjs(time).minute())
            .second(0);

          return combinedStart.isAfter(dayjs());
        },
      ),
    callOwner: Yup.string().required("Call owner is required"),
    subject: Yup.string()
      .max(100, "Subject cannot exceed 100 characters")
      .nullable(),
    callPurpose: Yup.string().nullable(),
    callAgenda: Yup.string()
      .max(500, "Call Agenda cannot exceed 500 characters")
      .nullable(),
  });
};

export const CALL_PURPOSE_OPTIONS = [
  { label: "Prospecting", value: "Prospecting", id: 1 },
  { label: "Administrative", value: "Administrative", id: 2 },
  { label: "Negotiation", value: "Negotiation", id: 3 },
  { label: "Demo", value: "Demo", id: 4 },
  { label: "Project", value: "Project", id: 5 },
  { label: "Desk", value: "Desk", id: 6 },
  { label: "None", value: "None", id: 7 },
];

export const fetchFullName = (item: any) => {
  const firstName = item?.first_name?.trim();
  const lastName = item?.last_name?.trim();

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;

  return "-";
};

export const getCallColumns = () => [
  {
    key: "subject",
    label: "Subject",
    render: (row: any) => row.subject || "-",
  },
  {
    key: "call_start_time",
    label: "Call Start Time",
    render: (row: any) =>
      row.call_start_time
        ? `${formatDate(row.call_start_time)} at ${formatTime(row.call_start_time)}`
        : "-",
  },
  {
    key: "call_owner",
    label: "Call Owner",
    render: (row: any) => fetchFullName(row?.callOwner) || "-",
  },
  {
    key: "call_purpose",
    label: "Call Purpose",
    render: (row: any) => row.call_purpose || "-",
  },
  {
    key: "call_agenda",
    label: "Call Agenda",
    render: (row: any) => row.call_agenda || "-",
  },
];

export const CALL_RESULT_OPTIONS = [
  { id: 1, label: "Interested", value: "Interested" },
  { id: 2, label: "Not Interested", value: "Not Interested" },
  { id: 3, label: "No Response/Busy", value: "No Response/Busy" },
  { id: 4, label: "Requested More Info", value: "Requested more info" },
  { id: 5, label: "Requested Call Back", value: "Requested call back" },
  { id: 6, label: "Invalid Number", value: "Invalid number" },
  { id: 7, label: "None", value: "None" },
];

export const RESCHEDULE_INITIAL_VALUES = {
  contact: "",
  rescheduleDate: null,
  rescheduleTime: null,
  notes: "",
};
export const RESCHEDULE_VALIDATION_SCHEMA = Yup.object().shape({
  contact: Yup.string()
    .required("Contact is required")
    .typeError("Invalid contact"),
  rescheduleDate: Yup.date().required("Reschedule date is required"),
  rescheduleTime: Yup.date()
    .required("Reschedule time is required")
    .typeError("Invalid reschedule time")
    .test(
      "future-time",
      "Reschedule time must be in the future",
      function (time) {
        const { rescheduleDate } = this.parent;
        if (!rescheduleDate || !time) return true;

        const combinedStart = dayjs(rescheduleDate)
          .hour(dayjs(time).hour())
          .minute(dayjs(time).minute())
          .second(0);

        return combinedStart.isAfter(dayjs());
      },
    ),
  notes: Yup.string().max(500, "Notes cannot exceed 500 characters").nullable(),
});
export const CANCEL_INITIAL_VALUES = {
  contact: "",
  notes: "",
};
export const CANCEL_VALIDATION_SCHEMA = Yup.object().shape({
  contact: Yup.string().required("Contact is required"),
  notes: Yup.string().max(500, "Notes cannot exceed 500 characters").nullable(),
});

export const callTypeOptions = [
  { value: "Outbound", label: "Outbound", id: 1 },
  { value: "Inbound", label: "Inbound", id: 2 },
  { value: "Missed", label: "Missed", id: 3 },
];
